import { useState, useEffect, ImgHTMLAttributes } from 'react';

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  priority?: boolean;
  className?: string;
}

/**
 * OptimizedImage component with lazy loading, blur placeholder, and responsive srcset support.
 * 
 * Usage:
 * - For regular images: <OptimizedImage src={myImage} alt="..." />
 * - For responsive srcset: <OptimizedImage src={myImage} srcSet={myImageSrcSet} sizes="..." alt="..." />
 * - For above-the-fold images: <OptimizedImage src={myImage} priority alt="..." />
 */
const OptimizedImage = ({
  src,
  srcSet,
  sizes = '(max-width: 480px) 480px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, (max-width: 1280px) 1280px, 1920px',
  alt,
  width,
  height,
  priority = false,
  className = '',
  ...props
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);

  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    const img = document.querySelector(`[data-optimized-src="${src}"]`);
    if (img) {
      observer.observe(img);
    }

    return () => observer.disconnect();
  }, [src, priority]);

  return (
    <img
      data-optimized-src={src}
      src={isInView ? src : undefined}
      srcSet={isInView && srcSet ? srcSet : undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : 'auto'}
      onLoad={() => setIsLoaded(true)}
      className={`${className} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      {...props}
    />
  );
};

export default OptimizedImage;
